import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Skeleton, Text } from '../../../../components';
import { formatCount } from '../../format';
import type { QualityBreakdown, QualityCounts } from '../../quality/types';
import {
  Frame,
  Title,
  Sub,
  Scroll,
  Table,
  HeadCell,
  Row,
  RowHead,
  Cell,
  Off,
  Note,
  Toggle,
} from './TrafficBreakdown.styles';

type BreakdownRow = {
  key: Exclude<keyof QualityBreakdown, 'peopleOverlap'>;
  /** i18n key. */
  name: string;
  /** Shown when collapsed too. */
  lead?: boolean;
  /** A category the service declares and does not count yet. */
  off?: boolean;
};

const ROWS: readonly BreakdownRow[] = [
  { key: 'primary', name: 'Primary', lead: true },
  { key: 'other', name: 'Other / unclassified' },
  { key: 'suspected', name: 'Suspected automation' },
  { key: 'excluded', name: 'Excluded', lead: true },
  { key: 'confirmedTest', name: 'Confirmed tests' },
  { key: 'verifiedBot', name: 'Verified bots', off: true },
  { key: 'staff', name: 'Staff', off: true },
  { key: 'all', name: 'All recorded', lead: true },
];

const COLUMNS: readonly { key: keyof QualityCounts; name: string }[] = [
  { key: 'pageViews', name: 'Page views' },
  { key: 'visits', name: 'Visits' },
  { key: 'people', name: 'People' },
];

export type TrafficBreakdownProps = {
  /** The report's breakdown; null until the first report lands. */
  breakdown: QualityBreakdown | null;
  /** `meta.rulesVersion` of the same report. */
  rulesVersion: string;
  loading?: boolean;
};

export type BreakdownTableProps = {
  breakdown: QualityBreakdown;
  rulesVersion: string;
  expanded: boolean;
  onToggle?: () => void;
};

/** The breakdown's table and its notes, collapsed to Primary, Excluded and All recorded or with every row. */
export const BreakdownTable = ({
  breakdown,
  rulesVersion,
  expanded,
  onToggle,
}: BreakdownTableProps): React.ReactElement => {
  const { i18n } = useTranslation();
  return (
    <Frame>
      <Title text="Traffic breakdown" tag="h3" />
      <Sub text="All selected hosts and dates, before page, source, link, campaign, and country filters" tag="span" />
      <Scroll>
        <Table>
          <thead>
            <tr>
              <HeadCell scope="col" />
              {COLUMNS.map((c) => (
                <HeadCell key={c.key} scope="col" $num>
                  <Text text={c.name} tag="span" />
                </HeadCell>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.filter((r) => expanded || r.lead).map((r) => (
              <Row key={r.key}>
                <RowHead scope="row">
                  <Text text={r.name} tag="span" />
                  {r.off ? <Off text="not enabled" tag="span" /> : null}
                </RowHead>
                {COLUMNS.map((c) => (
                  <Cell key={c.key}>{formatCount(breakdown[r.key][c.key], i18n.language)}</Cell>
                ))}
              </Row>
            ))}
          </tbody>
        </Table>
      </Scroll>
      {expanded ? (
        <>
          <Note
            text="A person can have both counted and excluded visits, so people do not add up across rows."
            tag="paragraph"
          />
          <Note text="All recorded people = counted only + counted and excluded + excluded only" tag="paragraph" />
          <Note text="Rules version: {{version}}" placeholders={{ version: rulesVersion }} tag="paragraph" />
        </>
      ) : null}
      <Toggle>
        <Button variant="text" label={expanded ? 'Show less' : 'Show more'} onClick={onToggle} />
      </Toggle>
    </Frame>
  );
};

/** A counts-only table of every category of the selected hosts and dates, before the fine filters. */
export const TrafficBreakdown = ({
  breakdown,
  rulesVersion,
  loading = false,
}: TrafficBreakdownProps): React.ReactElement => {
  const [expanded, setExpanded] = React.useState(false);
  if (loading || !breakdown) return <Skeleton block height={160} radius={16} />;
  return (
    <BreakdownTable
      breakdown={breakdown}
      rulesVersion={rulesVersion}
      expanded={expanded}
      onToggle={() => setExpanded((open) => !open)}
    />
  );
};

export default TrafficBreakdown;
